<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CreateToolForm from './CreateToolForm.vue';

const props = defineProps({
  server: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const isOpen = ref(false);
const dialogRef = ref(null);
const formRef = ref(null);

const uiFlags = useMapGetter('wgptMcpServers/getUIFlags');

const handleSubmit = async () => {
  if (!formRef.value) return;
  const { state, validate } = formRef.value;

  try {
    const isFormValid = await validate();
    if (!isFormValid) return;

    await store.dispatch('wgptMcpServers/addTool', {
      serverId: props.server.id,
      ...state,
    });
    useAlert(t('WGPT_MCP_SERVERS.EDIT.TOOLS.CREATE_TOOL.API.SUCCESS_MESSAGE'));
    dialogRef.value.close();
    emit('close');
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_MCP_SERVERS.EDIT.TOOLS.CREATE_TOOL.API.ERROR_MESSAGE')
    );
  }
};

const handleDismiss = () => {
  isOpen.value = false;
  emit('close');
};

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    dialogRef.value?.open();
  });
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('WGPT_MCP_SERVERS.EDIT.TOOLS.CREATE_TOOL.TITLE')"
    :is-loading="uiFlags.isUpdating"
    :disable-confirm-button="uiFlags.isUpdating"
    :confirm-button-label="
      t('WGPT_MCP_SERVERS.EDIT.TOOLS.CREATE_TOOL.FORM.SAVE')
    "
    :cancel-button-label="
      t('WGPT_MCP_SERVERS.EDIT.TOOLS.CREATE_TOOL.FORM.CANCEL')
    "
    overflow-y-auto
    @confirm="handleSubmit"
    @close="handleDismiss"
  >
    <CreateToolForm
      v-if="isOpen"
      ref="formRef"
      :server="server"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
